/* eslint-disable max-lines -- one coherent flow: plan, adjust, preview,
   consent, enroll. Splitting it further would scatter the state that every
   step shares (selection, preview bytes, chosen person). */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import {
  useRequestVoiceIdentification,
  useVoiceSamplePlan,
  useVoiceSamplePreview
} from '@utils/api/editors/voice-identifications';
import {
  useAddVoiceSample,
  useGetVoiceSamples,
  useReplaceVoiceSample
} from '@utils/api/organizations/speakers/voice-profiles';
import { Speaker } from '@utils/types/organization/speaker.ts';
import {
  VOICE_CONSENT_NOTICE_VERSION,
  VoiceSamplePreview,
  VoiceSampleRange
} from '@utils/types/organization/voice-profile.ts';
import {
  evaluateSelection,
  formatSelectionDuration,
  TimelineLabel
} from '@utils/editors/voice-sample-ranges.ts';
import { isUncertainVoiceSave } from '@utils/editors/voice-profile-errors.ts';
import { VoiceSampleRangeEditor } from './voice-sample-range-editor.tsx';
import { VoiceSamplePreviewPlayer } from './voice-sample-preview-player.tsx';
import { VoiceSampleConsent } from './voice-sample-consent.tsx';
import { VoiceErrorMessage } from './voice-error-message.tsx';
import { VoiceSampleReplacePicker } from './voice-sample-replace-picker.tsx';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  documentId: string;
  /** Original diarization label whose speech is being sampled. */
  speakerLabel: string;
  /** That label's source spans, used to offer extra passages. */
  label?: TimelineLabel;
  /** The directory person this sample belongs to. */
  person: Speaker;
  recognitionAvailable?: boolean;
  minSampleSeconds: number;
  maxSampleSeconds: number;
  maxSamplesPerSpeaker: number;
}

/** Save a voice sample for a directory person from editor audio.
 *
 * The admin never prepares a file: the backend plans a clean selection, cuts
 * it server-side and hands back a preview whose exact bytes are what gets
 * enrolled. Adding preserves previous samples; replacing is a separate action
 * that names the sample being overwritten. */
export const SaveVoiceSampleDialog = ({
  isOpen,
  onClose,
  sessionId,
  documentId,
  speakerLabel,
  label,
  person,
  recognitionAvailable = true,
  minSampleSeconds,
  maxSampleSeconds,
  maxSamplesPerSpeaker
}: Props) => {
  const { t } = useTranslation();

  const [ranges, setRanges] = useState<VoiceSampleRange[]>([]);
  const [preview, setPreview] = useState<VoiceSamplePreview | null>(null);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [sampleLabel, setSampleLabel] = useState('');
  const [replaceTargetId, setReplaceTargetId] = useState<number | null>(null);
  const [saveUncertain, setSaveUncertain] = useState(false);
  // The passage editor is hidden by default — the backend's clean-speech plan
  // is almost always what the user wants; only reveal the range controls when
  // they explicitly ask to edit.
  const [passagesExpanded, setPassagesExpanded] = useState(false);
  const requestGeneration = useRef(0);

  const plan = useVoiceSamplePlan(sessionId, documentId);
  const previewRequest = useVoiceSamplePreview(sessionId, documentId);
  const addSample = useAddVoiceSample();
  const replaceSample = useReplaceVoiceSample();
  const samples = useGetVoiceSamples(person.id, isOpen);
  const requestRecognition = useRequestVoiceIdentification(
    sessionId,
    documentId
  );

  const enrolledCount = samples.data?.count ?? 0;
  const atSampleLimit = enrolledCount >= maxSamplesPerSpeaker;

  const requestPlan = () => {
    const generation = ++requestGeneration.current;
    setPreview(null);
    setConsentConfirmed(false);
    // Per-call mutation observer callbacks can be reassigned by reset/replay.
    // Bind each response to its own promise and source generation instead.
    void plan
      .mutateAsync({ speakerLabel })
      .then(result => {
        if (requestGeneration.current === generation) setRanges(result.ranges);
      })
      .catch(() => {
        // The mutation exposes the error for the retry explanation below.
      });
  };

  // Ask the backend for a clean starting selection when the dialog opens.
  // Reset everything first so a reopen never shows a previous person's bytes.
  useEffect(() => {
    if (!isOpen) return;
    setRanges([]);
    setPreview(null);
    setConsentConfirmed(false);
    setSampleLabel('');
    setReplaceTargetId(null);
    setSaveUncertain(false);
    setPassagesExpanded(false);
    plan.reset();
    previewRequest.reset();
    addSample.reset();
    replaceSample.reset();
    requestPlan();
    return () => {
      requestGeneration.current += 1;
    };
    // Mutation objects change on every render. Only a new source, person or
    // opening needs a fresh plan; the hook shares concurrent requests.
  }, [isOpen, sessionId, documentId, speakerLabel, person.id]);

  const selection = evaluateSelection(
    ranges,
    minSampleSeconds,
    maxSampleSeconds
  );

  // A new selection invalidates the preview: the bytes enrolled must always be
  // the bytes the admin actually heard.
  const handleRangesChange = (next: VoiceSampleRange[]) => {
    requestGeneration.current += 1;
    setRanges(next);
    setPreview(null);
    setConsentConfirmed(false);
    previewRequest.reset();
  };

  const requestPreview = () => {
    if (selection.problem) return;
    const generation = ++requestGeneration.current;
    setPreview(null);
    setConsentConfirmed(false);
    void previewRequest
      .mutateAsync({ ranges: selection.ranges })
      .then(result => {
        if (requestGeneration.current === generation) setPreview(result);
      })
      .catch(() => {
        // Preserve the selection; the mutation supplies its specific error.
      });
  };

  const finishSave = () => {
    const message = t('voice-sample-saved', { name: person.name });
    // Recognition is only worth offering when the backend's thresholds are
    // calibrated AND this session can be reprocessed. Otherwise the saved
    // sample only matters for future recordings, which the toast implies.
    if (recognitionAvailable) {
      toast.success(
        tst => (
          <span className="flex items-center gap-3">
            <span>{message}</span>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(tst.id);
                requestRecognition.mutate();
              }}
              className="cursor-pointer font-semibold underline"
            >
              {t('voice-run-recognition-now')}
            </button>
          </span>
        ),
        { duration: 10000 }
      );
    } else {
      toast.success(message);
    }
    onClose();
  };

  const handleSaveError = (error: unknown) => {
    // The outcome is genuinely unknown (no response, or a gateway error): the
    // sample may already be stored, so re-read the metadata before the user
    // retries and risks enrolling the same speech twice.
    if (isUncertainVoiceSave(error)) {
      setSaveUncertain(true);
      void samples.refetch();
    }
  };

  const consent = {
    confirmed: consentConfirmed,
    noticeVersion: VOICE_CONSENT_NOTICE_VERSION
  };

  const handleAdd = () => {
    if (!preview || !consentConfirmed || atSampleLimit || busy) return;
    addSample.mutate(
      {
        speakerId: person.id,
        bytes: preview.bytes,
        label: sampleLabel.trim() || null,
        consent
      },
      { onSuccess: finishSave, onError: handleSaveError }
    );
  };

  const handleReplace = () => {
    if (!preview || !consentConfirmed || replaceTargetId == null || busy)
      return;
    replaceSample.mutate(
      {
        speakerId: person.id,
        profileId: replaceTargetId,
        bytes: preview.bytes,
        label: sampleLabel.trim() || null,
        consent
      },
      { onSuccess: finishSave, onError: handleSaveError }
    );
  };

  const isSaving = addSample.isLoading || replaceSample.isLoading;
  const busy = isSaving || previewRequest.isLoading || plan.isLoading;
  const saveError = addSample.error ?? replaceSample.error;

  const replacing = replaceTargetId != null;
  // Duplicate submissions are disabled while a save is in flight, and after an
  // uncertain save until the refreshed sample metadata has landed — so a retry
  // is made against what is actually stored, not against a stale count.
  const canSave =
    !!preview &&
    consentConfirmed &&
    !busy &&
    !samples.isFetching &&
    (!atSampleLimit || replacing);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('voice-save-sample')}
      caption={t('voice-save-sample-caption', { name: person.name })}
      customWidth="min(620px, calc(100vw - 2rem))"
      customMaxHeight="calc(100dvh - 2rem)"
      closeOnOutsideClick={false}
      footerActions={[
        <Button
          key="cancel"
          variant="secondary"
          onClick={onClose}
          label={t('cancel')}
          disabled={isSaving}
        />,
        <Button
          key="save"
          onClick={replacing ? handleReplace : handleAdd}
          // No icon: the Button atom hides the label below 1080px when one is
          // present, and this is the dialog's primary action.
          label={
            replacing ? t('voice-replace-sample') : t('voice-add-voice-sample')
          }
          loading={isSaving}
          disabled={!canSave || (replacing && replaceTargetId == null)}
        />
      ]}
    >
      <div className="space-y-4">
        {!recognitionAvailable && (
          <Text as="p" size="sm" tone="muted">
            {t('voice-error-not-calibrated')}
          </Text>
        )}
        <Text as="p" size="sm" tone="muted">
          {t('voice-save-sample-explanation', {
            min: minSampleSeconds,
            max: maxSampleSeconds
          })}
        </Text>

        {/* The plan request failed: nothing usable to start from. The reason
            (too little clean speech, unknown label, busy) is the actionable
            part, and planning can be retried without losing the person. */}
        {plan.isError && (
          <VoiceErrorMessage error={plan.error} onRetry={requestPlan} />
        )}

        {plan.isLoading && (
          <Text as="p" size="sm" tone="muted">
            {t('voice-planning-selection')}
          </Text>
        )}

        {/* Collapsed by default: the backend's cut is almost always right,
            and the ticket calls for "manual passage adjustment behind an
            edit option" so the admin isn't shown range controls they will
            never touch. Expanding reveals the full range editor. */}
        <div className="rounded-md border p-2">
          <button
            type="button"
            onClick={() => setPassagesExpanded(current => !current)}
            aria-expanded={passagesExpanded}
            className="flex w-full items-center justify-between gap-2 text-left cursor-pointer"
            disabled={busy}
          >
            <span className="flex items-center gap-2">
              {passagesExpanded ? (
                <ChevronDown size={14} aria-hidden />
              ) : (
                <ChevronRight size={14} aria-hidden />
              )}
              <Text as="span" size="sm" weight="medium">
                {t('voice-edit-passages')}
              </Text>
            </span>
            <Text as="span" size="xs" tone="muted">
              {t('voice-selected-total', {
                duration: formatSelectionDuration(selection.durationMs)
              })}
            </Text>
          </button>

          {passagesExpanded && (
            <div className="mt-2">
              <VoiceSampleRangeEditor
                ranges={ranges}
                onChange={handleRangesChange}
                label={label}
                disabled={busy}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            size="small"
            label={
              preview ? t('voice-refresh-preview') : t('voice-create-preview')
            }
            onClick={requestPreview}
            loading={previewRequest.isLoading}
            disabled={!!selection.problem || busy}
          />
        </div>

        {/* Why the selection cannot be submitted, in the user's terms. */}
        {selection.problem && (
          <Text as="p" size="sm" tone="danger">
            {t(selection.problem, {
              min: minSampleSeconds,
              max: maxSampleSeconds
            })}
          </Text>
        )}

        <VoiceErrorMessage
          error={previewRequest.error}
          onRetry={requestPreview}
        />

        {preview && <VoiceSamplePreviewPlayer preview={preview} />}

        {preview && (
          <VoiceSampleConsent
            personName={person.name}
            consentConfirmed={consentConfirmed}
            onConsentChange={setConsentConfirmed}
            sampleLabel={sampleLabel}
            onSampleLabelChange={setSampleLabel}
            disabled={isSaving}
          />
        )}

        {/* At the limit, adding is impossible but replacing is not — so the
            dialog says so and offers the explicit replacement instead. */}
        <VoiceSampleReplacePicker
          samples={samples.data?.profiles ?? []}
          enrolledCount={enrolledCount}
          maxSamples={maxSamplesPerSpeaker}
          atLimit={atSampleLimit}
          selectedId={replaceTargetId}
          onSelect={setReplaceTargetId}
          disabled={isSaving}
        />

        {/* The save may or may not have landed. Say so, and show the refreshed
            count, so a retry is a decision rather than a guess. */}
        {saveUncertain && !samples.isFetching && (
          <Text as="p" size="sm" tone="danger">
            {t('voice-save-uncertain', { count: enrolledCount })}
          </Text>
        )}

        <VoiceErrorMessage error={saveError} />
      </div>
    </Modal>
  );
};
